import { useState, useEffect } from 'react'
import { useEndpoints } from '../hooks/useApi'
import { apiClient } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import type { CommandType, PublishResponse, PublishRequest } from '../types/api'

// Extended command type for UI presets
type ExtendedCommandType = CommandType | 'SILENCE_5S' | 'SILENCE_10S' | 'SILENCE_30S'

interface FormData {
  endpoint: string
  deviceId: string
  command: ExtendedCommandType | ''
  repeat: number
  sensorRepeat: number
  wavFiles: string[]
  firmwareFile: string
  confFile: string // MG+CONF用のファイル指定
  ersflSeq: string // MG+ERSFL用のシーケンス番号
  ersflSafeFile: string // MG+ERSFL用のセーフファイル名
}

interface ControlPanelProps {
  initialData?: {
    deviceId: string
    endpoint: string
  } | null
  onDataUsed?: () => void
}

export default function ControlPanel({ initialData, onDataUsed }: ControlPanelProps) {
  const { user } = useAuth()
  const isAdmin = user?.role === 'admin'
  const { data: endpointsData } = useEndpoints()

  const [formData, setFormData] = useState<FormData>({
    endpoint: '',
    deviceId: '',
    command: '',
    repeat: 1,
    sensorRepeat: 0,
    wavFiles: [''],
    firmwareFile: '',
    confFile: 'ping.txt',
    ersflSeq: '89',
    ersflSafeFile: 'safe_file.txt',
  })

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isCooldown, setIsCooldown] = useState(false)
  const [result, setResult] = useState<{
    success: boolean
    response?: PublishResponse
    error?: string
  } | null>(null)

  // Handle initial data from devices list
  useEffect(() => {
    if (initialData && initialData.deviceId && initialData.endpoint && endpointsData) {
      // Verify the endpoint exists and is connected
      const endpoint = endpointsData.endpoints[initialData.endpoint]
      const finalEndpoint =
        endpoint && endpoint.connected
          ? initialData.endpoint
          : Object.entries(endpointsData.endpoints).find(([_, ep]) => ep.connected)?.[0] ||
            initialData.endpoint

      setFormData(prev => ({
        ...prev,
        deviceId: initialData.deviceId,
        endpoint: finalEndpoint,
      }))
      onDataUsed?.()
    }
  }, [initialData, onDataUsed, endpointsData])

  // Auto-select first connected endpoint for Power users
  // Only run when there's no initialData (direct access to control panel)
  useEffect(() => {
    if (!isAdmin && endpointsData && !formData.endpoint && !initialData) {
      const firstConnected = Object.entries(endpointsData.endpoints).find(
        ([_, ep]) => ep.connected
      )
      if (firstConnected) {
        setFormData(prev => ({
          ...prev,
          endpoint: firstConnected[0],
        }))
      }
    }
  }, [isAdmin, endpointsData, formData.endpoint, initialData])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.endpoint || !formData.deviceId || !formData.command) return

    // Prevent double-click during cooldown or submission
    if (isCooldown || isSubmitting) {
      return
    }

    setIsSubmitting(true)
    setResult(null)

    try {
      // Convert UI preset commands to actual MQTT commands
      let actualCommand: CommandType = formData.command as CommandType
      if (
        formData.command === 'SILENCE_5S' ||
        formData.command === 'SILENCE_10S' ||
        formData.command === 'SILENCE_30S'
      ) {
        actualCommand = 'MG+PLAY'
      }

      const request: PublishRequest = {
        endpoint: formData.endpoint,
        mago_id: formData.deviceId,
        command: actualCommand,
        repeat: formData.repeat,
        sensor_repeat: formData.sensorRepeat,
        wav_files:
          actualCommand === 'MG+OTA'
            ? formData.firmwareFile
              ? [formData.firmwareFile]
              : []
            : actualCommand === 'MG+CONF' ||
                actualCommand === 'MG+SC0' ||
                actualCommand === 'MG+ERSFL'
              ? [] // MG+CONF、MG+ERSFLはpayloadフィールド、MG+SC0はbackendでseq_id採番
              : formData.wavFiles.filter(f => f.trim() !== ''),
        payload:
          actualCommand === 'MG+CONF'
            ? `MG+CONF=93,${formData.confFile}`
            : actualCommand === 'MG+ERSFL'
              ? `MG+ERSFL=89,${formData.ersflSafeFile}`
              : undefined,
      }

      const response = await apiClient.publishMessage(request)

      setResult({
        success: true,
        response,
      })

      // Reset form but keep endpoint, deviceId, and command for consecutive operations
      setFormData(prev => ({
        ...prev,
        repeat: 1,
        sensorRepeat: 0,
        wavFiles: [''],
        firmwareFile: '',
        confFile: 'ping.txt',
        ersflSeq: '89',
        ersflSafeFile: 'safe_file.txt',
      }))
    } catch (error) {
      setResult({
        success: false,
        error: error instanceof Error ? error.message : 'コマンド送信に失敗しました',
      })
    } finally {
      setIsSubmitting(false)
      // Start cooldown period (2 seconds)
      setIsCooldown(true)
      setTimeout(() => {
        setIsCooldown(false)
      }, 2000)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: name === 'repeat' || name === 'sensorRepeat' ? Number.parseInt(value) || 0 : value,
      }

      // Set default files for commands
      if (name === 'command') {
        if (value === 'MG+PLAY') {
          newData.wavFiles = ['test-normal.wav']
        } else if (value === 'SILENCE_5S') {
          newData.wavFiles = ['silence-5s.wav']
        } else if (value === 'SILENCE_10S') {
          newData.wavFiles = ['silence-10s.wav']
        } else if (value === 'SILENCE_30S') {
          newData.wavFiles = ['silence-30s.wav']
        } else if (value === 'MG+SC') {
          newData.wavFiles = ['test-anpi.wav']
        } else if (value === 'MG+OTA') {
          newData.firmwareFile = 'MGFW_V0500.bin'
        } else if (value === 'MG+CONF') {
          newData.confFile = 'ping.txt'
        } else if (value === 'MG+ERSFL') {
          newData.ersflSeq = '89'
          newData.ersflSafeFile = 'safe_file.txt'
        }
      }

      return newData
    })
  }

  const handleWavFileChange = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      wavFiles: prev.wavFiles.map((file, i) => (i === index ? value : file)),
    }))
  }

  const addWavFile = () => {
    if (formData.wavFiles.length < 10) {
      setFormData(prev => ({
        ...prev,
        wavFiles: [...prev.wavFiles, ''],
      }))
    }
  }

  const removeWavFile = (index: number) => {
    if (formData.wavFiles.length > 1) {
      setFormData(prev => ({
        ...prev,
        wavFiles: prev.wavFiles.filter((_, i) => i !== index),
      }))
    }
  }

  const renderCommandForm = () => {
    if (!formData.command) return null

    switch (formData.command) {
      case 'MG+PLAY':
      case 'SILENCE_5S':
      case 'SILENCE_10S':
      case 'SILENCE_30S':
      case 'MG+SC':
        return (
          <>
            <div className="row">
              <div className="col-md-6">
                <div className="mb-3">
                  <label htmlFor="repeat-input" className="form-label">
                    リピート回数 <span className="text-muted">(1-10)</span>
                  </label>
                  <input
                    id="repeat-input"
                    type="number"
                    className="form-control"
                    name="repeat"
                    value={formData.repeat}
                    onChange={handleInputChange}
                    min="1"
                    max="10"
                    required
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">
                    センサーリピート回数 <span className="text-muted">(0-10)</span>
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    name="sensorRepeat"
                    value={formData.sensorRepeat}
                    onChange={handleInputChange}
                    min="0"
                    max="10"
                    required
                  />
                </div>
              </div>
            </div>
            <div className="mb-3">
              <label className="form-label">
                音声ファイル <span className="text-muted">(最大10個)</span>
              </label>
              {formData.wavFiles.map((file, index) => (
                <div key={`wav-file-${index}-${file || 'empty'}`} className="input-group mb-2">
                  <input
                    type="text"
                    className="form-control"
                    value={file}
                    onChange={e => handleWavFileChange(index, e.target.value)}
                    placeholder={`ファイル${index + 1}.wav`}
                  />
                  <button
                    type="button"
                    className="btn btn-outline-danger"
                    onClick={() => removeWavFile(index)}
                    disabled={formData.wavFiles.length === 1}
                  >
                    <i className="fas fa-trash" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="btn btn-outline-primary btn-sm"
                onClick={addWavFile}
                disabled={formData.wavFiles.length >= 10}
              >
                <i className="fas fa-plus me-2" />
                ファイル追加
              </button>
            </div>
          </>
        )

      case 'MG+OTA':
        return (
          <div className="mb-3">
            <label className="form-label">
              ファームウェアファイル <span className="text-muted">(.bin)</span>
            </label>
            <input
              type="text"
              className="form-control"
              name="firmwareFile"
              value={formData.firmwareFile}
              onChange={handleInputChange}
              placeholder="MGFW_V0500.bin"
              required
            />
          </div>
        )

      case 'MG+CONF':
        return (
          <div className="mb-3">
            <label htmlFor="conf-file" className="form-label">
              設定ファイル <span className="text-muted">(強制再起動用)</span>
            </label>
            <input
              id="conf-file"
              type="text"
              className="form-control"
              name="confFile"
              value={formData.confFile}
              onChange={handleInputChange}
              placeholder="ping.txt"
              required
            />
            <div className="form-text">コマンド形式: MG+CONF=93,{formData.confFile}</div>
          </div>
        )

      case 'MG+SC0':
        return (
          <div className="mb-3">
            <div className="alert alert-info">
              <i className="fas fa-info-circle me-2" />
              <strong>安否モード解除</strong>
              <br />
              デバイスの安否確認モードを解除します。
              <br />
              コマンド形式: <code>MG+SC=&#123;seq_id&#125;,0</code>
            </div>
          </div>
        )

      case 'MG+ERSFL':
        return (
          <div className="mb-3">
            <label htmlFor="ersfl-safe-file" className="form-label">
              セーフファイル名
            </label>
            <input
              id="ersfl-safe-file"
              type="text"
              className="form-control"
              name="ersflSafeFile"
              value={formData.ersflSafeFile}
              onChange={handleInputChange}
              placeholder="safe_file.txt"
              required
            />
            <div className="form-text">コマンド形式: MG+ERSFL=89,{formData.ersflSafeFile}</div>
          </div>
        )

      default:
        return null
    }
  }

  return (
    <div className="card mqtt-control">
      <div className="card-header">
        <h5>
          <i className="fas fa-satellite-dish me-2" />
          MQTT Control Panel
        </h5>
      </div>
      <div className="card-body">
        <form onSubmit={handleSubmit}>
          {/* Endpoint Selection */}
          <div className="row">
            {isAdmin && (
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">
                    <i className="fas fa-server me-2" />
                    MQTT Endpoint
                  </label>
                  <select
                    className="form-select"
                    name="endpoint"
                    value={formData.endpoint}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">エンドポイントを選択...</option>
                    {endpointsData &&
                      Object.entries(endpointsData.endpoints).map(([key, endpoint]) => (
                        <option key={key} value={key} disabled={!endpoint.connected}>
                          {key} ({endpoint.host}:{endpoint.port}) {endpoint.connected ? '🟢' : '🔴'}
                        </option>
                      ))}
                  </select>
                </div>
              </div>
            )}
            <div className={isAdmin ? 'col-md-6' : 'col-12'}>
              <div className="mb-3">
                <label className="form-label">
                  <i className="fas fa-microchip me-2" />
                  Device ID
                </label>
                <input
                  type="text"
                  className="form-control"
                  name="deviceId"
                  value={formData.deviceId}
                  onChange={handleInputChange}
                  placeholder="MAGO001"
                  required
                />
              </div>
            </div>
          </div>

          {/* Command Selection */}
          <div className="mb-3">
            <label htmlFor="command-select" className="form-label">
              <i className="fas fa-terminal me-2" />
              コマンド
            </label>
            <select
              id="command-select"
              className="form-select"
              name="command"
              value={formData.command}
              onChange={handleInputChange}
              required
            >
              <option value="">コマンドを選択...</option>
              <option value="MG+PLAY">MG+PLAY - 通常音声再生</option>
              <option value="SILENCE_5S">MG+PLAY - 無音再生（5秒）</option>
              <option value="SILENCE_10S">MG+PLAY - 無音再生（10秒）</option>
              <option value="SILENCE_30S">MG+PLAY - 無音再生（30秒）</option>
              <option value="MG+SC">MG+SC - 安全確認モード</option>
              {isAdmin && (
                <>
                  <option value="MG+SC0">MG+SC0 - 安否モード解除</option>
                  <option value="MG+OTA">MG+OTA - ファームウェア更新</option>
                  <option value="MG+CONF">MG+CONF - 強制再起動</option>
                  <option value="MG+ERSFL">MG+ERSFL - セーフファイル削除</option>
                </>
              )}
            </select>
          </div>

          {/* Command-specific Form */}
          {renderCommandForm()}

          {/* Submit Button */}
          <button
            type="submit"
            className={`btn ${isCooldown ? 'btn-secondary' : 'btn-primary'}`}
            disabled={
              isSubmitting ||
              isCooldown ||
              !formData.endpoint ||
              !formData.deviceId ||
              !formData.command
            }
          >
            {isSubmitting ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                />
                送信中...
              </>
            ) : isCooldown ? (
              <>
                <i className="fas fa-clock me-2" />
                送信待機中...
              </>
            ) : (
              <>
                <i className="fas fa-paper-plane me-2" />
                メッセージ送信
              </>
            )}
          </button>
        </form>

        {/* Result Display */}
        {result && (
          <div
            className={`alert ${result.success ? 'alert-success' : 'alert-danger'} mt-4`}
            role="alert"
          >
            {result.success && result.response ? (
              <div>
                <h6>
                  <i className="fas fa-check-circle me-2" />
                  送信成功
                </h6>
                <div className="row">
                  <div className="col-md-6">
                    <strong>Topic:</strong> <code>{result.response.topic}</code>
                    <br />
                    <strong>Endpoint:</strong> {result.response.endpoint}
                    <br />
                    <strong>Group ID:</strong> {result.response.group_id}
                  </div>
                  <div className="col-md-6">
                    <strong>Sequence ID:</strong> {result.response.seq_id}
                    <br />
                    <strong>ステータス:</strong> {result.response.status}
                  </div>
                </div>
                <div className="mt-2">
                  <strong>Payload:</strong>
                  <br />
                  <code className="d-block p-2 bg-light">{result.response.payload}</code>
                </div>
              </div>
            ) : (
              <div>
                <i className="fas fa-exclamation-triangle me-2" />
                {result.error}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
